import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ValuationPreferences } from '../../services/api/clientService';
import './SettingsComponents.css';

interface ValuationPreferencesSettingsProps {
  settings: ValuationPreferences;
  onSave: (settings: ValuationPreferences) => void;
  saving: boolean;
}

// Custom dropdown — native <select> popup ignores dark-mode CSS in Chrome/Windows
interface SelectOption { value: string; label: string; }

const CustomSelect: React.FC<{
  value: string;
  options: SelectOption[];
  onChange: (v: string) => void;
}> = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const label = options.find(o => o.value === value)?.label ?? value;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', width: '240px', flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%',
          padding: '0.5rem 0.75rem',
          border: '1px solid var(--border-color)',
          borderRadius: '6px',
          fontSize: '0.9rem',
          background: 'var(--input-background)',
          color: 'var(--text-primary)',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          textAlign: 'left',
        }}
      >
        <span>{label}</span>
        <span style={{ fontSize: '0.65rem', opacity: 0.6, marginLeft: '0.5rem' }}>▼</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 3px)',
          left: 0,
          right: 0,
          background: 'var(--bg-secondary, #1e1e2e)',
          border: '1px solid var(--border-color)',
          borderRadius: '6px',
          zIndex: 200,
          overflow: 'hidden',
          boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
        }}>
          {options.map((opt, i) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); }}
              style={{
                display: 'block',
                width: '100%',
                padding: '0.55rem 0.75rem',
                border: 'none',
                borderBottom: i < options.length - 1 ? '1px solid var(--border-color)' : 'none',
                background: opt.value === value ? 'var(--primary-color)' : 'transparent',
                color: opt.value === value ? '#fff' : 'var(--text-primary)',
                cursor: 'pointer',
                textAlign: 'left',
                fontSize: '0.9rem',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ——————————————————————————————————————————————

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'max-content 240px 1fr',
  columnGap: '1.5rem',
  alignItems: 'center',
  borderTop: '1px solid var(--border-color)',
};

const cellStyle: React.CSSProperties = {
  padding: '0.875rem 0',
};

const dividerStyle: React.CSSProperties = {
  gridColumn: '1 / -1',
  borderBottom: '1px solid var(--border-color)',
  margin: 0,
};

const labelStyle: React.CSSProperties = {
  fontWeight: 500,
  fontSize: '0.9rem',
  color: 'var(--text-primary)',
  whiteSpace: 'nowrap',
};

const helpStyle: React.CSSProperties = {
  fontSize: '0.82rem',
  color: 'var(--text-secondary)',
  lineHeight: 1.45,
};

const cardBoxStyle: React.CSSProperties = {
  flex: 1,
  background: 'var(--bg-secondary, rgba(255,255,255,0.03))',
  borderRadius: '8px',
  padding: '1rem 1.25rem',
  border: '1px solid var(--border-color)',
};

const cardTitleStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: '0.875rem',
  color: 'var(--text-primary)',
  marginBottom: '0.6rem',
};

const ValuationPreferencesSettings: React.FC<ValuationPreferencesSettingsProps> = ({
  settings,
  onSave,
  saving,
}) => {
  const { t } = useTranslation();
  // Normalise legacy values to canonical ones so the UI and saved values stay in sync
  const normalise = (s: ValuationPreferences): ValuationPreferences => ({
    ...s,
    spotDateMode:  (s.spotDateMode === 'curve_date' || !s.spotDateMode)   ? 'execution_date' : s.spotDateMode,
    ufSpotDateMode: (s.ufSpotDateMode === 'inherit' || !s.ufSpotDateMode) ? 'execution_date' : s.ufSpotDateMode,
  });
  const [formData, setFormData] = useState<ValuationPreferences>(() => normalise(settings));
  const [hasChanges, setHasChanges] = useState(
    settings.spotDateMode === 'curve_date' || settings.ufSpotDateMode === 'inherit'
  );

  const handleChange = (field: keyof ValuationPreferences, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = () => { onSave(formData); setHasChanges(false); };
  const handleReset = () => { setFormData(normalise(settings)); setHasChanges(false); };

  const fwdRateOpts: SelectOption[] = [
    { value: 'cirp',       label: t('admin.valuation.forwardRateSourceCirp', 'Derived from IR Curves') },
    { value: 'fwd_points', label: t('admin.valuation.forwardRateSourceFwdPoints', 'Uploaded Forward Points') },
  ];
  const discountingOpts: SelectOption[] = [
    { value: 'two_leg', label: t('admin.valuation.discountingMethodTwoLeg', 'Two-Leg') },
    { value: 'net_pnl', label: t('admin.valuation.discountingMethodNetPnl', 'Netted Cashflow') },
  ];
  const fwdPointsOpts: SelectOption[] = [
    { value: 'market', label: t('admin.valuation.fwdPointsPriceMarket', 'Market (Ask/Bid)') },
    { value: 'mid',    label: t('admin.valuation.fwdPointsPriceMid', 'Mid') },
    { value: 'bid',    label: t('admin.valuation.fwdPointsPriceBid', 'Bid') },
    { value: 'ask',    label: t('admin.valuation.fwdPointsPriceAsk', 'Ask') },
  ];

  const fwdRateHelp: Record<string, string> = {
    cirp:       t('admin.valuation.forwardRateSourceCirpHelp', 'Calculates the theoretical forward rate from the uploaded IR curves using Covered Interest Rate Parity.'),
    fwd_points: t('admin.valuation.forwardRateSourceFwdPointsHelp', 'Reads the forward rate directly from the uploaded forward points curve.'),
  };
  const discountingHelp: Record<string, string> = {
    two_leg: t('admin.valuation.discountingMethodTwoLegHelp', 'Discounts each currency leg (Ccy1 and Ccy2) independently at its own curve rate, then converts to the valuation currency at the spot rate on the valuation date.'),
    net_pnl: t('admin.valuation.discountingMethodNetPnlHelp', 'Nets both legs into a single cashflow in the valuation currency and discounts to PV using that currency\'s curve.'),
  };
  const fwdPointsPriceHelp: Record<string, string> = {
    market: t('admin.valuation.fwdPointsPriceMarketHelp', 'Ask for Buy trades, Bid for Sell trades.'),
    mid:    t('admin.valuation.fwdPointsPriceMidHelp', 'Average of Bid and Ask.'),
    bid:    t('admin.valuation.fwdPointsPriceBidHelp', 'Always use the Bid side, regardless of trade direction.'),
    ask:    t('admin.valuation.fwdPointsPriceAskHelp', 'Always use the Ask side, regardless of trade direction.'),
  };
  const asiaticoOpts: SelectOption[] = [
    { value: 'blended',     label: t('admin.valuation.asiaticoMethodBlended', 'Blended (Fixing Schedule)') },
    { value: 'single_rate', label: t('admin.valuation.asiaticoMethodSingleRate', 'Single Rate (Maturity)') },
  ];
  const asiaticoHelp: Record<string, string> = {
    blended:     t('admin.valuation.asiaticoMethodBlendedHelp', 'Averages per-date rates from the fixing schedule: realised rates for past dates, projected rates for future dates.'),
    single_rate: t('admin.valuation.asiaticoMethodSingleRateHelp', 'Ignores the fixing schedule and projects a single forward rate to the maturity date, like a vanilla forward.'),
  };
  const fwdStartingEligibilityOpts: SelectOption[] = [
    { value: 'confirmed_only',      label: t('admin.valuation.fwdStartingEligibilityConfirmedOnly', 'Only Confirmed') },
    { value: 'include_provisional', label: t('admin.valuation.fwdStartingEligibilityIncludeProvisional', 'Include Provisional') },
  ];
  const fwdStartingEligibilityHelp: Record<string, string> = {
    confirmed_only:      t('admin.valuation.fwdStartingEligibilityConfirmedOnlyHelp', 'Only value forward starting trades in Confirmed / ContractSigned / Final — current behaviour.'),
    include_provisional: t('admin.valuation.fwdStartingEligibilityIncludeProvisionalHelp', 'Also include forward starting trades at Booked with provisional confirmation status.'),
  };
  const fwdStartingMethodOpts: SelectOption[] = [
    { value: 'single_rate', label: t('admin.valuation.fwdStartingMethodSingleRate', 'Single Rate') },
    { value: 'blended',     label: t('admin.valuation.fwdStartingMethodBlended', 'Blended (Fixing Schedule)') },
  ];
  const fwdStartingMethodHelp: Record<string, string> = {
    single_rate: t('admin.valuation.fwdStartingMethodSingleRateHelp', 'Projects a single spot rate to the observation date using the forward curve, then adds the agreed forward points.'),
    blended:     t('admin.valuation.fwdStartingMethodBlendedHelp', 'For trades with multiple fixing dates: blends realised observations with projected rates for remaining dates. For single-fixing trades, same as Single Rate.'),
  };

  const splitMtmLevelOpts: SelectOption[] = [
    { value: 'parent', label: t('admin.valuation.splitMtmLevelParent', 'Parent Trades') },
    { value: 'split',  label: t('admin.valuation.splitMtmLevelSplit', 'Split Trades') },
  ];
  const splitMtmLevelHelp: Record<string, string> = {
    parent: t('admin.valuation.splitMtmLevelParentHelp', 'MtM is calculated on parent (bank-facing) trades. Split children are excluded from valuation.'),
    split:  t('admin.valuation.splitMtmLevelSplitHelp', 'MtM is calculated on each split child trade individually. Parent trades with splits are excluded from valuation.'),
  };

  const valuationDateModeHelp: Record<string, string> = {
    system_date:              t('admin.valuation.valuationDateModeSystemDateHelp', 'Day counts, trade eligibility, and the stored valuation date all use the current execution date.'),
    previous_business_day:    t('admin.valuation.valuationDateModePrevBDHelp', 'As-of date is the previous business day. Trades entered on the execution date are excluded from valuation.'),
    end_of_previous_month:    t('admin.valuation.valuationDateModeEndOfPrevMonthHelp', 'As-of date is the last business day of the month preceding the execution date. Use this for month-end close: any execution date within a month resolves to the same as-of date.'),
  };
  const marketDataDateModeHelp: Record<string, string> = {
    system_date:        t('admin.valuation.marketDataDateModeSystemDateHelp', 'IR curves and forward point curves are loaded for the execution date.'),
    valuation_date:     t('admin.valuation.marketDataDateModeValDateHelp', 'IR curves and forward point curves are loaded for the as-of date.'),
    first_business_day: t('admin.valuation.marketDataDateModeFirstBDHelp', 'IR curves and forward point curves are loaded for the first business day of the execution date\'s month. Useful for month-end close: ensures the same curves regardless of which day the valuation is run.'),
  };
  const ufSpotDateModeHelp: Record<string, string> = {
    inherit:        t('admin.valuation.ufSpotDateModeExecutionDateHelp', 'Uses the UF published for the execution date.'),
    execution_date: t('admin.valuation.ufSpotDateModeExecutionDateHelp', 'Uses the UF published for the execution date.'),
    as_of_date:     t('admin.valuation.ufSpotDateModeAsOfDateHelp', 'Uses the UF published for the as-of date.'),
    furthest_known: t('admin.valuation.ufSpotDateModeFurthestKnownHelp', 'Uses the UF for the 9th of the relevant month (Chilean convention). If the execution date is on or before the 9th, uses the 9th of that month; if after the 9th, uses the 9th of the following month.'),
    first_business_day: t('admin.valuation.ufSpotDateModeFirstBDHelp', 'Uses the UF published for the first business day of the execution date\'s month. Useful for month-end close: ensures the same UF rate regardless of which day the valuation is run.'),
  };
  const spotDateModeHelp: Record<string, string> = {
    curve_date:     t('admin.valuation.spotDateModeExecutionDateHelp', 'Spot rates are fetched for the execution date.'),
    execution_date: t('admin.valuation.spotDateModeExecutionDateHelp', 'Spot rates are fetched for the execution date, even when curves come from an earlier as-of date. Use this when clients apply today\'s market spot to prior-date curves.'),
    as_of_date:     t('admin.valuation.spotDateModeAsOfDateHelp', 'Spot rates are fetched for the as-of date.'),
    first_business_day: t('admin.valuation.spotDateModeFirstBDHelp', 'Spot rates are fetched for the first business day of the execution date\'s month. Useful for month-end close: ensures the same spot rate regardless of which day the valuation is run.'),
  };

  return (
    <div className="settings-section">
      <div className="settings-section-header">
        <h2>{t('admin.valuation.title', 'Valuation Preferences')}</h2>
        <p>{t('admin.valuation.description', 'Default methodology settings for MtM calculations')}</p>
      </div>

      <div style={gridStyle}>
        {/* Forward Rate Method */}
        <span style={{ ...labelStyle, ...cellStyle }}>{t('admin.valuation.forwardRateSource', 'Forward Rate Method')}</span>
        <div style={cellStyle}><CustomSelect value={formData.forwardRateSource} options={fwdRateOpts} onChange={v => handleChange('forwardRateSource', v)} /></div>
        <span style={{ ...helpStyle, ...cellStyle }}>{fwdRateHelp[formData.forwardRateSource]}</span>
        <div style={dividerStyle} />

        {/* Quote Side — only shown when forward points is selected */}
        {formData.forwardRateSource === 'fwd_points' && <>
          <span style={{ ...labelStyle, ...cellStyle }}>{t('admin.valuation.fwdPointsPrice', 'Quote Side')}</span>
          <div style={cellStyle}><CustomSelect value={formData.fwdPointsPrice} options={fwdPointsOpts} onChange={v => handleChange('fwdPointsPrice', v)} /></div>
          <span style={{ ...helpStyle, ...cellStyle }}>{fwdPointsPriceHelp[formData.fwdPointsPrice]}</span>
          <div style={dividerStyle} />
        </>}

        {/* Discounting Method */}
        <span style={{ ...labelStyle, ...cellStyle }}>{t('admin.valuation.discountingMethod', 'Discounting Method (Entrega Física)')}</span>
        <div style={cellStyle}><CustomSelect value={formData.discountingMethod} options={discountingOpts} onChange={v => handleChange('discountingMethod', v)} /></div>
        <span style={{ ...helpStyle, ...cellStyle }}>{discountingHelp[formData.discountingMethod]}</span>
        <div style={dividerStyle} />

        {/* Forward Asiático Method */}
        <span style={{ ...labelStyle, ...cellStyle }}>{t('admin.valuation.asiaticoMethod', 'Forward Asiático Method')}</span>
        <div style={cellStyle}><CustomSelect value={formData.asiaticoMethod} options={asiaticoOpts} onChange={v => handleChange('asiaticoMethod', v)} /></div>
        <span style={{ ...helpStyle, ...cellStyle }}>{asiaticoHelp[formData.asiaticoMethod]}</span>
        <div style={dividerStyle} />

        {/* Forward Starting Eligibility */}
        <span style={{ ...labelStyle, ...cellStyle }}>{t('admin.valuation.fwdStartingEligibility', 'Forward Starting Eligibility')}</span>
        <div style={cellStyle}><CustomSelect value={formData.forwardStartingEligibility} options={fwdStartingEligibilityOpts} onChange={v => handleChange('forwardStartingEligibility', v)} /></div>
        <span style={{ ...helpStyle, ...cellStyle }}>{fwdStartingEligibilityHelp[formData.forwardStartingEligibility]}</span>
        <div style={dividerStyle} />

        {/* Forward Starting Method — only shown when eligibility = include_provisional */}
        {formData.forwardStartingEligibility === 'include_provisional' && <>
          <span style={{ ...labelStyle, ...cellStyle }}>{t('admin.valuation.fwdStartingMethod', 'Forward Starting Method')}</span>
          <div style={cellStyle}><CustomSelect value={formData.forwardStartingMethod} options={fwdStartingMethodOpts} onChange={v => handleChange('forwardStartingMethod', v)} /></div>
          <span style={{ ...helpStyle, ...cellStyle }}>{fwdStartingMethodHelp[formData.forwardStartingMethod]}</span>
          <div style={dividerStyle} />
        </>}

        {/* Split MtM Level */}
        <span style={{ ...labelStyle, ...cellStyle }}>{t('admin.valuation.splitMtmLevel', 'Split Trade MtM Level')}</span>
        <div style={cellStyle}><CustomSelect value={formData.splitMtmLevel} options={splitMtmLevelOpts} onChange={v => handleChange('splitMtmLevel', v)} /></div>
        <span style={{ ...helpStyle, ...cellStyle }}>{splitMtmLevelHelp[formData.splitMtmLevel]}</span>
        <div style={dividerStyle} />

        {/* Valuation Currency */}
        <span style={{ ...labelStyle, ...cellStyle }}>{t('admin.valuation.valuationCurrency', 'Valuation Currency')}</span>
        <div style={cellStyle}>
          <input
            type="text"
            value={formData.valuationCurrency}
            onChange={(e) => handleChange('valuationCurrency', e.target.value.toUpperCase().slice(0, 3))}
            maxLength={3}
            placeholder="CLP"
            style={{
              width: '240px',
              padding: '0.5rem 0.75rem',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              fontSize: '0.9rem',
              background: 'var(--input-background)',
              color: 'var(--text-primary)',
              fontFamily: 'monospace',
              letterSpacing: '0.05em',
              boxSizing: 'border-box',
            }}
          />
        </div>
        <span style={{ ...helpStyle, ...cellStyle }}>{t('admin.valuation.valuationCurrencyHelp', 'ISO 4217 currency code. MtM results are expressed in this currency; trades settled in a different currency are converted at the spot rate on the valuation date.')}</span>
        <div style={dividerStyle} />

        {/* ─── Date Configuration Section ─── */}
        <div style={{ gridColumn: '1 / -1', paddingTop: '1.25rem', paddingBottom: '0.5rem' }}>
          <h3 style={{ margin: '0 0 0.35rem', fontSize: '0.92rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            {t('admin.valuation.dateConfiguration', 'Date Configuration')}
          </h3>
          <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.5, maxWidth: '56rem' }}>
            {t('admin.valuation.dateConfigurationDesc', 'Controls which dates are used as the as-of date, curve date, spot rate date, and UF rate date during valuation. The Execution Date is the date you run the valuation.')}
          </p>
        </div>
        <div style={dividerStyle} />

        {/* As-of Date — radio buttons */}
        <span style={{ ...labelStyle, ...cellStyle }}>{t('admin.valuation.asOfDate', 'As-of Date')}</span>
        <div style={{ ...cellStyle, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {([
            { value: 'system_date',            label: t('admin.valuation.asOfDateSameAsExecution', 'Same as execution date') },
            { value: 'previous_business_day',  label: t('admin.valuation.asOfDatePrevBD', 'Previous Business Day') },
            { value: 'end_of_previous_month',  label: t('admin.valuation.asOfDateEndOfPrevMonth', 'End of Previous Month') },
          ] as { value: string; label: string }[]).map(opt => (
            <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="radio"
                name="valuationDateMode"
                checked={formData.valuationDateMode === opt.value}
                onChange={() => handleChange('valuationDateMode', opt.value)}
                style={{ accentColor: 'var(--primary-color)', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>{opt.label}</span>
            </label>
          ))}
        </div>
        <span style={{ ...helpStyle, ...cellStyle }}>{valuationDateModeHelp[formData.valuationDateMode]}</span>
        <div style={dividerStyle} />

        {/* Market Data Sources — three cards side by side */}
        {(() => {
          const spotEffective = (formData.spotDateMode === 'curve_date' || !formData.spotDateMode)
            ? 'execution_date' : formData.spotDateMode;
          const ufEffective = (formData.ufSpotDateMode === 'inherit' || !formData.ufSpotDateMode)
            ? 'execution_date' : formData.ufSpotDateMode;
          const executionLabel = t('admin.valuation.executionDate', 'Execution Date');
          const asOfLabel      = t('admin.valuation.asOfDate', 'As-of Date');
          const mkRadio = (name: string, val: string, checked: boolean, label: string, onChange: () => void) => (
            <label key={val} style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', cursor: 'pointer', marginBottom: '0.3rem' }}>
              <input type="radio" name={name} checked={checked} onChange={onChange}
                style={{ accentColor: 'var(--primary-color)', cursor: 'pointer' }} />
              <span style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>{label}</span>
            </label>
          );
          return (
            <div style={{ gridColumn: '1 / -1', padding: '0.875rem 0', display: 'flex', gap: '1rem' }}>
              {/* Curves */}
              <div style={cardBoxStyle}>
                <div style={cardTitleStyle}>{t('admin.valuation.curves', 'Curves')}</div>
                {mkRadio('marketDataDateMode', 'system_date',        formData.marketDataDateMode === 'system_date',        executionLabel, () => handleChange('marketDataDateMode', 'system_date'))}
                {mkRadio('marketDataDateMode', 'valuation_date',     formData.marketDataDateMode === 'valuation_date',     asOfLabel,      () => handleChange('marketDataDateMode', 'valuation_date'))}
                {mkRadio('marketDataDateMode', 'first_business_day', formData.marketDataDateMode === 'first_business_day', t('admin.valuation.firstBusinessDay', '1st Business Day'), () => handleChange('marketDataDateMode', 'first_business_day'))}
                <p style={{ ...helpStyle, margin: '0.35rem 0 0' }}>{marketDataDateModeHelp[formData.marketDataDateMode]}</p>
              </div>
              {/* Spot Rate */}
              <div style={cardBoxStyle}>
                <div style={cardTitleStyle}>{t('admin.valuation.spotRate', 'Spot Price')}</div>
                {mkRadio('spotDateMode', 'execution_date',    spotEffective === 'execution_date',    executionLabel, () => handleChange('spotDateMode', 'execution_date'))}
                {mkRadio('spotDateMode', 'as_of_date',        spotEffective === 'as_of_date',        asOfLabel,      () => handleChange('spotDateMode', 'as_of_date'))}
                {mkRadio('spotDateMode', 'first_business_day', spotEffective === 'first_business_day', t('admin.valuation.firstBusinessDay', '1st Business Day'), () => handleChange('spotDateMode', 'first_business_day'))}
                <p style={{ ...helpStyle, margin: '0.35rem 0 0' }}>{spotDateModeHelp[spotEffective]}</p>
              </div>
              {/* UF Rate */}
              <div style={cardBoxStyle}>
                <div style={cardTitleStyle}>{t('admin.valuation.ufRate', 'UF Value')}</div>
                {mkRadio('ufSpotDateMode', 'execution_date',    ufEffective === 'execution_date',    executionLabel,                                () => handleChange('ufSpotDateMode', 'execution_date'))}
                {mkRadio('ufSpotDateMode', 'as_of_date',        ufEffective === 'as_of_date',        asOfLabel,                                    () => handleChange('ufSpotDateMode', 'as_of_date'))}
                {mkRadio('ufSpotDateMode', 'first_business_day', ufEffective === 'first_business_day', t('admin.valuation.firstBusinessDay', '1st Business Day'), () => handleChange('ufSpotDateMode', 'first_business_day'))}
                {mkRadio('ufSpotDateMode', 'furthest_known',    ufEffective === 'furthest_known',    t('admin.valuation.nextNinth', 'Next 9th'), () => handleChange('ufSpotDateMode', 'furthest_known'))}
                <p style={{ ...helpStyle, margin: '0.35rem 0 0' }}>{ufSpotDateModeHelp[ufEffective]}</p>
              </div>
            </div>
          );
        })()}
      </div>

      <div className="settings-actions">
        <button onClick={handleReset} disabled={!hasChanges || saving} className="btn-secondary">
          {t('common.resetChanges', 'Reset Changes')}
        </button>
        <button onClick={handleSave} disabled={!hasChanges || saving} className="btn-primary">
          {saving ? t('common.saving', 'Saving...') : t('common.saveChanges', 'Save Changes')}
        </button>
      </div>
    </div>
  );
};

export default ValuationPreferencesSettings;
